import { useState } from "react";
import { Link } from "react-router";
import { api } from "../api";
import { cleanPhone } from "../functions/cleanPhone";
import { delay } from "../functions/delay";
import { Button } from "./tailwind/button";
import { Divider } from "./tailwind/divider";
import { Field } from "./tailwind/fieldset";
import { Input } from "./tailwind/input";
import { Text } from "./tailwind/text";
import { useCompanyUsers } from "../externalPages/settings/company/hooks";

export function SendATest({ user }) {
  const { users, isLoading } = useCompanyUsers();
  const testUsers = users
    .filter((u) => !!u.phone)
    ?.map((u) => ({
      label: `${u.phone} (${u.firstName} ${u.lastName})`,
      phone: u.phone,
    }));
  const [isTestSendingOngoing, setIsTestSendingOngoing] = useState(false);
  const [showFailureWarning, setShowFailureWarning] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState("");

  const userForTesting =
    user && user.phone && /@(getsimio|alcmeon)\.com$/.test(user.email)
      ? [
          {
            label: `${user.phone} (${user.firstName} ${user.lastName})`,
            phone: user.phone,
          },
        ]
      : [];

  const campaignId = location.pathname.split("/").slice(-1)[0];

  const updateLastTestPhoneNumbers = async (
    userId,
    testPhoneNumber,
    currentNumbers,
  ) => {
    let numbersArray = currentNumbers || [];
    numbersArray = numbersArray.map((item) => cleanPhone(item));
    const isDuplicate = numbersArray.some((item) => {
      if (Array.isArray(item)) {
        return item.includes(cleanPhone(testPhoneNumber));
      }
      return item === cleanPhone(testPhoneNumber);
    });

    if (isDuplicate) {
      return numbersArray;
    }
    const updatedNumbers = [...numbersArray, cleanPhone(testPhoneNumber)];

    if (updatedNumbers.length > 5) {
      updatedNumbers.shift();
    }

    await api.user.update(userId, { lastTestPhoneNumbers: updatedNumbers });
    return updatedNumbers;
  };

  const handleSendTest = async () => {
    setIsTestSendingOngoing(true);
    const campaignId = location.pathname.split("/").slice(-1)[0];
    const sendingDate = new Date();

    await api.sending.create({
      status: "ongoing",
      campaign: { _link: campaignId },
      channel: "whatsapp",
      sendingDate,
      sendingBatchId: sendingDate.getTime(),
      customer: { _link: user.customerId },
      contactList: testPhoneNumber.split(",").map((phone) => cleanPhone(phone)),
      isForced: true,
      isTest: true,
    });

    updateLastTestPhoneNumbers(
      user.id,
      testPhoneNumber,
      user.lastTestPhoneNumbers,
    )
      .then((updatedNumbers) => console.log(updatedNumbers))
      .catch(console.error);

    setIsTestSendingOngoing(false);
    await delay(3000);
    setShowFailureWarning(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && testPhoneNumber) {
      handleSendTest();
    }
  };

  return (
    <>
      <Field>
        <Text className="text-sm font-semibold mb-1">Phone numbers</Text>
        <Text className="text-xs mb-1">
          You can type multiple phone numbers separated by commas
        </Text>
        <div className="flex items-center space-x-3">
          <Input
            className="flex-grow"
            name="Phone numbers"
            placeholder="33601020304,33611121314"
            value={testPhoneNumber}
            onChange={(e) =>
              setTestPhoneNumber(e.target.value.replace(/[^0-9,]/g, ""))
            }
            onKeyDown={handleKeyDown}
            autoComplete="off"
            maxLength={2000}
          />
          <Button
            className="flex-shrink-0"
            color="primaryButton"
            disabled={!testPhoneNumber}
            loading={isTestSendingOngoing}
            onClick={handleSendTest}
          >
            Send a test
          </Button>
        </div>
        {user.lastTestPhoneNumbers && (
          <div className="my-4">
            <Text className="text-sm font-semibold mb-1">
              Last phone numbers used as test:
            </Text>
            <ul className="list-disc list-inside">
              {user.lastTestPhoneNumbers
                .slice()
                .reverse()
                .map((number, index) => (
                  <li key={index} className="flex items-center gap-3 mt-2">
                    <Button
                      color="outline"
                      className="h-6 hover:bg-gray-200 text-gray-500 w-full"
                      onClick={() => {
                        let writtenNumbers = testPhoneNumber
                          ? testPhoneNumber.split(",")
                          : [];
                        console.log(writtenNumbers);
                        console.log(typeof number);
                        console.log(number);
                        let selectedNumbers = Array.isArray(number)
                          ? number
                          : number.split(",");

                        selectedNumbers.forEach((num) => {
                          if (!writtenNumbers.includes(num)) {
                            writtenNumbers.push(num);
                          } else {
                            // remove the number
                            writtenNumbers = writtenNumbers.filter(
                              (n) => n !== num,
                            );
                          }
                        });
                        setTestPhoneNumber(writtenNumbers.join(","));
                      }}
                    >
                      <span
                        className="text-gray-800 pt-1 w-[80%] overflow-hidden"
                        style={{ overflowWrap: "break-word" }}
                      >
                        {Array.isArray(number)
                          ? number.join(", ")
                          : number.split(",").join(", ")}{" "}
                        {testPhoneNumber.includes(number) && "✅"}
                      </span>
                    </Button>
                  </li>
                ))}
            </ul>
          </div>
        )}
        {!isLoading &&
          (testUsers || userForTesting) &&
          userForTesting.concat(testUsers).length > 0 && (
            <div className="my-4">
              <Text className="text-sm font-semibold mb-1">Company users:</Text>
              <ul className="list-disc list-inside">
                {userForTesting.concat(testUsers).map((testUser, index) => (
                  <li key={index} className="flex items-center gap-3 mt-2">
                    <Button
                      color="outline"
                      className="h-6 hover:bg-gray-200 text-gray-500 w-full"
                      onClick={() =>
                        testPhoneNumber.includes(testUser.phone)
                          ? setTestPhoneNumber(
                              testPhoneNumber
                                .replace(`,${testUser.phone}`, "")
                                .replace(`${testUser.phone},`, "")
                                .replace(`${testUser.phone}`, ""),
                            )
                          : setTestPhoneNumber(
                              (testPhoneNumber || "").concat(
                                testPhoneNumber
                                  ? `,${testUser.phone}`
                                  : testUser.phone,
                              ),
                            )
                      }
                    >
                      <span
                        className="text-gray-800 pt-1 w-[80%] overflow-hidden"
                        style={{ overflowWrap: "break-word" }}
                      >
                        {testUser.label}{" "}
                        {testPhoneNumber.includes(testUser.phone) && "✅"}
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

        {showFailureWarning && (
          <>
            <Divider />
            <Text className="text-xs mt-5">
              If you do not receive your test after a few seconds, check the
              reason for the failure{" "}
              <Link className="underline" to={`/analytics/${campaignId}`}>
                in the analytics
              </Link>
              .
            </Text>
            <Text className="text-xs mt-3">
              You may also check that the media is correctly imported and that
              the variable is filled in.
            </Text>
          </>
        )}
      </Field>
    </>
  );
}
